import print from 'print-js';

import { initGrid } from 'utils/grid/grid';

import api from 'dashboard/resources/current-liability.api.ts';

import { fetchLiabilities } from 'manage-inventory/actions/fetch-liabilities.actions';
import { downloadManageInventoryReportByTab } from 'manage-inventory/actions/download-report-by-tab.actions';

import template from './inventory-liability-tab.html';

const mapStateToThis = ({ manageInventory, ui, config, dictionary, session }) => {
  const { pagination, activeTab } = ui;
  const { isLoading, liabilities } = manageInventory;
  const { pageSizeKey, sortColumnKey, defaultPageSizes, usesPortionedPacks } = config;
  const { locale } = session;
  const terms = dictionary.terms[locale];

  return {
    terms,
    activeTab,
    isLoading,
    pagination,
    pageSizeKey,
    liabilities,
    sortColumnKey,
    defaultPageSizes,
    usesPortionedPacks,
  };
};

const mapDispatchToThis = {
  fetchLiabilities,
  downloadManageInventoryReportByTab,
};

// eslint-disable-next-line no-undef
@Inject('$ngRedux', '$stateParams', 'gettextCatalog', 'tableService')
class InventoryLiabilityTabController {
  constructor() {
    this.unsubscribe = this.$ngRedux.connect(mapStateToThis, mapDispatchToThis)(this);
    this.print = print;
  }

  $onInit() {
    this.tableConfig = this.tableService.initConfig({ configName: 'inventoryLiabilityTab' });
    this.actions = this.getActions();
    this.headers = this.tableConfig.getHeaders(this.getHeaders());
    this.defaultParams = {
      [this.pageSizeKey]: this.defaultPageSizes[2],
      retailerId: this.$stateParams.retailerNo,
    };
    this.gridModel = initGrid(this.fetchLiabilities, this.defaultParams);
    this.search();

    api.fetchCurrentLiability(this.$stateParams.retailerNo).then((value) => {
      this.outstandingAmount = value;
    });
  }

  $onDestroy() {
    this.unsubscribe();
  }

  clearFilters() {
    this.filters = {};
    this.applyFilters();
  }

  search() {
    if (this.liabilityFilters && this.liabilityFilters.$invalid) return;

    this.gridModel.applyFilters().then(() => {
      this.preserveFilters();
    });
  }

  clear() {
    this.gridModel.pagingModel.filters = { gameNumber: '' };
    this.search();
  }

  preserveFilters() {
    this.preservedFilters = { ...this.gridModel.pagingModel.filters };
  }

  getActions() {
    return [
      {
        type: 'download',
        function: this.onDownloadReport.bind(this),
        extension: 'xlsx',
      },
      {
        type: 'print',
        function: this.handlePrint.bind(this),
      },
    ];
  }

  getHeaders() {
    return [
      {
        text: this.gettextCatalog.getString('Game No.'),
        field: 'gameNumber',
        sortable: true,
      },
      {
        text: this.gettextCatalog.getString('Game Name'),
        field: 'gameName',
        sortable: true,
      },
      {
        /// {{ pack }} is Lottery term using to determine Pack.
        text: this.gettextCatalog.getString('{{ pack }} No.', { pack: this.terms.PACK.singular }),
        field: 'packNo',
        sortable: true,
        textAlignment: 'text-right',
      },
      this.usesPortionedPacks
        ? {
            /// {{ portion }} is Lottery term using to determine Portion.
            text: this.gettextCatalog.getString('{{ portion }} No.', { portion: this.terms.PORTION.singular }),
            field: 'portionNumber',
            sortable: true,
            textAlignment: 'text-right',
          }
        : null,
      this.usesPortionedPacks
        ? {
            text: this.gettextCatalog.getString('Ticket Range'),
            field: 'ticketRange',
            sortable: true,
            textAlignment: 'text-right',
          }
        : null,
      {
        text: this.gettextCatalog.getString('Price Point'),
        field: 'pieceValue',
        sortable: true,
        textAlignment: 'text-right',
      },
      {
        /// {{ pack }} is Lottery term using to determine Pack.
        text: this.usesPortionedPacks
          ? this.gettextCatalog.getString('{{ pack }}/{{ portion }} Value', {
              pack: this.terms.PACK.singular,
              portion: this.terms.PORTION.singular,
            })
          : this.gettextCatalog.getString('{{ pack }} Value', { pack: this.terms.PACK.singular }),
        field: 'packValue',
        sortable: true,
        textAlignment: 'text-right',
        /// {{ pack }} is Lottery term using to determine Pack.
        text: this.usesPortionedPacks
          ? this.gettextCatalog.getString('{{ pack }}/{{ portion }} Value', {
              pack: this.terms.PACK.singular,
              portion: this.terms.PORTION.singular,
            })
          : this.gettextCatalog.getString('{{ pack }} Value', { pack: this.terms.PACK.singular }),
        field: 'packValue',
        sortable: true,
        textAlignment: 'text-right',
      },
      {
        text: this.gettextCatalog.getString('Current Status'),
        field: 'currentStatus',
        sortable: true,
        textAlignment: 'text-right',
      },
      {
        text: this.gettextCatalog.getString('Est. % Sold'),
        field: 'soldOutPercent',
        sortable: true,
        textAlignment: 'text-right',
      },
      {
        text: this.gettextCatalog.getString('Days in Status'),
        field: 'daysInStatus',
        sortable: true,
        textAlignment: 'text-right',
      },
    ];
  }

  onDownloadReport() {
    const pagingModel = {
      ...this.gridModel.pagingModel,
      filters: this.preservedFilters,
      pageSize: 0, // BE contract - pageSize = 0 triggers exporting entire data set
    };

    const requestParams = {
      ...pagingModel,
      tabId: this.activeTab,
      retailerId: this.$stateParams.retailerNo,
    };

    return this.downloadManageInventoryReportByTab(requestParams);
  }

  handlePrint() {
    this.print({
      printable: 'inventory-liability-tab',
      type: 'html',
    });
  }
}

export default class InventoryLiabilityTabComponent {
  constructor() {
    this.controller = InventoryLiabilityTabController;
    this.template = template;
    this.bindings = {
      // TO-DO: Load current liability
      loadCurrentLiability: '&',
    };
    return this;
  }
}



// WEBPACK FOOTER //
// ./src/modules/manage-inventory/components/inventory-liability-tab/inventory-liability-tab-component.js